import { useState, useCallback, type ReactNode } from 'react'
import { ToastContext, type ToastType } from '@/hooks/useToast'

interface Toast {
  id: number
  message: string
  type: ToastType
}

let toastId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = ++toastId
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 3000)
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2.5 pointer-events-none">
        {toasts.map(toast => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

const TOAST_CONFIG = {
  success: {
    icon: 'check_circle',
    iconColor: 'text-emerald-500',
    accent: 'bg-emerald-500',
    border: 'border-[#e7ebf3] dark:border-white/10',
  },
  error: {
    icon: 'error',
    iconColor: 'text-red-500',
    accent: 'bg-red-500',
    border: 'border-red-100 dark:border-red-900/30',
  },
  info: {
    icon: 'info',
    iconColor: 'text-[#1754cf]',
    accent: 'bg-[#1754cf]',
    border: 'border-blue-100 dark:border-blue-900/30',
  },
} as const

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const cfg = TOAST_CONFIG[toast.type]

  return (
    <div
      className={`
        relative overflow-hidden flex items-center gap-3 pl-4 pr-10 py-3.5
        bg-white dark:bg-[#1c2230] rounded-xl border shadow-xl
        min-w-[300px] max-w-[380px] pointer-events-auto cursor-pointer
        animate-slideUp
        ${cfg.border}
      `}
      onClick={onClose}
      role="alert"
    >
      {/* Left color accent */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${cfg.accent}`} />

      <span className={`material-symbols-outlined text-[20px] shrink-0 ${cfg.iconColor}`}>
        {cfg.icon}
      </span>

      <span className="text-sm font-medium text-[#0d121b] dark:text-white leading-snug flex-1">
        {toast.message}
      </span>

      {/* Close button */}
      <button
        onClick={e => { e.stopPropagation(); onClose() }}
        className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-[#4c669a] hover:text-[#0d121b] dark:hover:text-white transition-colors rounded"
        aria-label="Dismiss"
      >
        <span className="material-symbols-outlined text-[16px]">close</span>
      </button>

      {/* Progress drain bar */}
      <div className={`absolute bottom-0 left-0 h-[2px] toast-progress ${cfg.accent} opacity-40`} />
    </div>
  )
}
